import type {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import { StandardApiResponse, HttpStatusCode } from '../types';
import { getJwtToken } from '@/utils/security';
import { getErrorMessage } from '../mappers/error.mapper';
import { getTraceId, setTraceId, TRACE_ID_HEADER } from '@/utils/traceId';

/**
 * Request interceptor for adding authentication token and trace ID
 * Adds X-Trace-Id header for distributed tracing across microservices
 * Note: Uses axios InternalAxiosRequestConfig only for axios interceptor compatibility
 */
export const authRequestInterceptor = (_clientName: string) => {
  return async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    // Add authentication token
    const token = getJwtToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add trace ID for request tracing
    const traceId = getTraceId();
    config.headers[TRACE_ID_HEADER] = traceId;

    return config;
  };
};

/**
 * Error handler for request interceptor
 * Note: Uses axios AxiosError only for axios interceptor compatibility
 */
export const authRequestErrorInterceptor = (error: AxiosError) => {
  return Promise.reject(error);
};

/**
 * Response interceptor - transforms all responses to standardized format
 * Extracts and updates trace ID from response headers if present
 * Always returns: { data: {}, error: {}, statuscode: number }
 * Note: Accepts AxiosResponse for axios compatibility, but returns StandardApiResponse
 */
export const authResponseInterceptor = <T = any>(
  response: AxiosResponse<T>
): StandardApiResponse<T, null> => {
  // Extract trace ID from response headers if present
  const responseTraceId = response.headers[TRACE_ID_HEADER.toLowerCase()];
  if (responseTraceId && typeof responseTraceId === 'string') {
    setTraceId(responseTraceId);
  }

  return {
    data: response.data,
    error: null,
    statuscode: response.status as HttpStatusCode,
    headers: response.headers,
    config: response.config,
  };
};

/**
 * Error handler for response interceptor
 * Transforms Axios errors to normalized application Error objects
 * Note: Rejects with Error that has statuscode, code, and details attached
 */
export const authResponseErrorInterceptor = (clientName: string) => {
  return (axiosError: AxiosError): Promise<never> => {
    const statuscode = (axiosError.response?.status || 0) as HttpStatusCode;

    // Extract error data from axios response (axios types it as {}, so we need to cast)
    const errorResponseData = axiosError.response?.data as
      | Record<string, any>
      | undefined;

    // Get user-friendly error message
    // Priority: API-provided message > Mapped message from status code > Default message
    const userFriendlyMessage =
      errorResponseData?.message ||
      getErrorMessage(statuscode).message ||
      'API request failed';

    // Create normalized Error object with attached metadata
    const appError = new Error(userFriendlyMessage) as Error & {
      statuscode?: HttpStatusCode;
      code?: string | number;
      details?: unknown;
    };

    appError.statuscode = statuscode;
    appError.code = errorResponseData?.code || axiosError.code || statuscode;
    appError.details = errorResponseData || {
      originalError: axiosError.message,
    };

    if (statuscode === 401) {
      console.error(
        `[${clientName}] Authentication failed - invalid or expired token`
      );
    }

    // Centralized error handling: reject with normalized Error
    // All downstream code can just catch(err) and use err.statuscode / err.message
    return Promise.reject(appError);
  };
};
